import { computeE1rmKg, e1rmAtEffort, E1RM_REP_CAP, weightForE1rm } from "./pr.js";
import { roundChainKg } from "./resolveDefaultWeight.js";

// #353: calibrating a MAX the athlete does not have (Antenore, 14/10). Sets
// of 5, more load each set, until one lands at RIR 2 or the sixth is done.
// The first load is the athlete's; this file proposes the rest, and reads the
// e1RM a calibration set is worth. Not a 1RM test: the brief forbids that.

export const CALIBRATION_REPS = 5;
export const CALIBRATION_STOP_RIR = 2;
export const CALIBRATION_MAX_SETS = 6;

/** A logged calibration set, in canonical kg. `rir` null: not recorded. */
export type CalibrationSet = { weightKg: number; reps: number; rir: number | null };

/** The e1RM of a calibration set: at its RIR (`e1rmAtEffort`), or from the
 *  reps alone when none was recorded (`computeE1rmKg`). A RIR past Epley's
 *  band is read at the band's edge: "at least 12 to failure". */
export const calibrationE1rmKg = (s: CalibrationSet): number | null => {
  if (s.rir === null) {
    return computeE1rmKg({ kind: "weighted", reps: s.reps, weight: s.weightKg });
  }
  const rir = Math.max(0, Math.min(s.rir, E1RM_REP_CAP - s.reps));
  return e1rmAtEffort(s.weightKg, s.reps, rir);
};

// The next set aims one rep above the stop (5 @ RIR 3), so the set after it
// can close the ramp. Epley's band already caps a jump near +10%.
const AIM_RIR = CALIBRATION_STOP_RIR + 1;
// "The load always goes up": at least 2.5%, and never under 1 kg.
const MIN_STEP_FRACTION = 0.025;
const MIN_STEP_KG = 1;

/** The load proposed for the set after `prev`, in kg. Always above it. Null
 *  when `prev` carries no load to start from. */
export const nextCalibrationKg = (prev: CalibrationSet): number | null => {
  if (!(prev.weightKg > 0)) return null;
  const e1rm = calibrationE1rmKg(prev);
  const aimed = e1rm === null ? null : weightForE1rm(e1rm, CALIBRATION_REPS + AIM_RIR);
  const floor = prev.weightKg + Math.max(prev.weightKg * MIN_STEP_FRACTION, MIN_STEP_KG);
  return roundChainKg(Math.max(aimed ?? 0, floor));
};

/** Does the ramp stop after this set? At RIR 2 or less (a fail counts as 0),
 *  or once `setsDone` calibration sets are logged and that makes six. */
export const isCalibrationOver = (last: {
  rir: number | null;
  fail: boolean;
  setsDone: number;
}): boolean =>
  last.fail ||
  (last.rir !== null && last.rir <= CALIBRATION_STOP_RIR) ||
  last.setsDone >= CALIBRATION_MAX_SETS;
